import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { format, isSameDay, isToday, isYesterday, parseISO } from "date-fns";
import { FileText, Film, ImageIcon, MessageSquareOff, Mic } from "lucide-react";
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";
import {
  WhatsappMessage,
  WhatsappSenderRole,
} from "@/modules/lead-page/dtos/index-v2";
import {
  useWhatsappAudioPlayback,
  WhatsappAudioPlaybackContext,
} from "@/modules/lead-page/new/hooks/use-whatsapp-audio-playback.hook";

const CARD_RADIUS = "12px";
/* Bubbles stay proportional in a narrow container (the activity drawer) but stop
   growing past this in a wide one (the Chats & Calls panel spans the viewport). */
const BUBBLE_MAX_WIDTH = "420px";

interface MessageGroup {
  senderRole: WhatsappSenderRole;
  senderName: string;
  messages: WhatsappMessage[];
}

const groupMessages = (messages: WhatsappMessage[]): MessageGroup[] => {
  const groups: MessageGroup[] = [];
  messages.forEach((msg) => {
    const last = groups[groups.length - 1];
    const sameSender =
      last &&
      last.senderRole === msg.senderRole &&
      last.senderName === msg.senderName &&
      Math.abs(
        parseISO(msg.time).getTime() -
          parseISO(last.messages[last.messages.length - 1].time).getTime(),
      ) <
        10 * 60 * 1000;
    if (sameSender) {
      last!.messages.push(msg);
    } else {
      groups.push({
        senderRole: msg.senderRole,
        senderName: msg.senderName,
        messages: [msg],
      });
    }
  });
  return groups;
};

/* -------------------------------------------------------------------------- */
/*  Day splitter                                                               */
/* -------------------------------------------------------------------------- */

const DaySplitter = ({ dayIso }: { dayIso: string }) => {
  const theme = useTheme();
  const date = parseISO(dayIso);
  const label = isToday(date)
    ? "Today"
    : isYesterday(date)
      ? "Yesterday"
      : format(date, "MMM d, yyyy");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: theme.nawy.spacing.sm,
        width: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: "1px",
          backgroundColor: theme.nawy.colors.semantic.border.secondary,
        }}
      />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: "16px",
          color: theme.nawy.colors.semantic.text.tertiary,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: "1px",
          backgroundColor: theme.nawy.colors.semantic.border.secondary,
        }}
      />
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                             */
/* -------------------------------------------------------------------------- */

interface MessageBubbleProps {
  msg: WhatsappMessage;
  isAgent: boolean;
  position: "only" | "first" | "middle" | "last";
  audioCtx?: WhatsappAudioPlaybackContext;
}

const SenderAvatar = ({
  name,
  isAgent,
}: {
  name: string;
  isAgent: boolean;
}) => {
  const theme = useTheme();
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isAgent
          ? theme.nawy.colors.semantic.indicator.green
          : theme.nawy.colors.semantic.indicator.navy,
        color: "#fff",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {initial}
    </Box>
  );
};

const getBubbleRadius = (
  isAgent: boolean,
  position: MessageBubbleProps["position"],
) => {
  const R = "14px";
  const tail = "4px";
  if (position === "middle")
    return isAgent ? `${R} ${tail} ${tail} ${R}` : `${tail} ${R} ${R} ${tail}`;
  if (position === "first")
    return isAgent ? `${R} ${R} ${tail} ${R}` : `${R} ${R} ${R} ${tail}`;
  if (position === "last")
    return isAgent ? `${R} ${tail} ${R} ${R}` : `${tail} ${R} ${R} ${R}`;
  return `${R} ${R} ${R} ${R}`;
};

const MEDIA_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  imageMessage: { icon: <ImageIcon size={16} />, label: "Image" },
  videoMessage: { icon: <Film size={16} />, label: "Video" },
  documentMessage: { icon: <FileText size={16} />, label: "Document" },
};

const MessageBubble = ({
  msg,
  isAgent,
  position,
  audioCtx,
}: MessageBubbleProps) => {
  const theme = useTheme();
  const [showTranscription, setShowTranscription] = useState(false);

  const isAudio = msg.messageType === "audioMessage";
  const audioId = audioCtx ? audioCtx.getAudioId(msg) : "";
  const mediaConfig = msg.messageType
    ? MEDIA_CONFIG[msg.messageType]
    : undefined;

  const bubbleSx = {
    maxWidth: "100%",
    width: isAudio ? "100%" : "auto",
    minWidth: 0,
    flex: isAudio ? 1 : "0 1 auto",
    px: theme.nawy.spacing.sm,
    py: theme.nawy.spacing.xs,
    borderRadius: getBubbleRadius(isAgent, position),
    background: (isAgent
      ? "#D9FDD4"
      : theme.nawy.colors.semantic.layer.primary) as string,
    border: "none",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  };

  const timestamp = (
    <Typography
      component="time"
      dateTime={msg.time}
      sx={{
        ...theme.nawy.typography.english.variants.body.sm.regular,
        fontSize: "11px",
        lineHeight: 1.2,
        alignSelf: "flex-end",
        marginTop: "2px",
        color: theme.nawy.colors.semantic.text.tertiary,
      }}
    >
      {format(parseISO(msg.time), "h:mm a")}
    </Typography>
  );

  const iconColor = isAgent
    ? theme.nawy.colors.semantic.indicator.green
    : theme.nawy.colors.semantic.text.secondary;

  const bubbleNode = (
    <Box sx={bubbleSx}>
      {isAudio ? (
        <>
          {msg.mediaUrl && audioCtx ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                "& > .MuiBox-root > .MuiBox-root": {
                  width: "100% !important",
                  maxWidth: "100% !important",
                },
              }}
            >
              <RecordingEntryDisplay
                isActivity
                recording={audioCtx.toRecordingEntry(msg)}
                isPlaying={audioCtx.isPlaying(audioId)}
                currentPlaybackTime={audioCtx.getCurrentTimeForId(audioId)}
                onTogglePlay={() => audioCtx.togglePlay(audioId, msg.mediaUrl!)}
                theme={theme}
                isLoading={
                  audioCtx.audioLoading &&
                  audioCtx.playingId === null &&
                  audioCtx.loadedId !== audioId
                }
                error={audioCtx.getErrorForId(audioId)}
                onSeek={(time) => audioCtx.seekTo(audioId, time)}
                playbackRate={audioCtx.playbackRate}
                onPlaybackRateChange={audioCtx.setPlaybackRate}
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Mic size={16} color={iconColor} />
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.secondary,
                }}
              >
                Voice message
              </Typography>
            </Box>
          )}
          {msg.message && (
            <Typography
              component="button"
              onClick={() => setShowTranscription((v) => !v)}
              sx={{
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                ...theme.nawy.typography.english.variants.body.sm.regular,
                fontSize: "11px",
                color: theme.nawy.colors.semantic.text.tertiary,
                textDecoration: "underline",
                "&:hover": { color: theme.nawy.colors.semantic.text.secondary },
              }}
            >
              {showTranscription ? "Hide transcription" : "Show transcription"}
            </Typography>
          )}
          {showTranscription && msg.message && (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.primary,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                borderTop: `1px solid ${isAgent ? "rgba(0,0,0,0.08)" : theme.nawy.colors.semantic.border.secondary}`,
                pt: "4px",
                mt: "2px",
              }}
            >
              {msg.message}
            </Typography>
          )}
          {timestamp}
        </>
      ) : mediaConfig ? (
        <>
          {msg.messageType === "imageMessage" && msg.mediaUrl ? (
            <Box
              component="img"
              src={msg.mediaUrl}
              alt={msg.message || "Image"}
              sx={{
                /* Fixed height, not max-height: the API sends no media
                   dimensions, so an image finishing its load above the reader
                   would otherwise grow and shove the whole thread down. */
                height: 260,
                maxWidth: "100%",
                minWidth: 140,
                borderRadius: "8px",
                objectFit: "cover",
                display: "block",
                backgroundColor: "rgba(16, 24, 40, 0.06)",
              }}
            />
          ) : msg.messageType === "videoMessage" && msg.mediaUrl ? (
            <Box
              component="video"
              src={msg.mediaUrl}
              controls
              sx={{
                /* Same as images: fixed height so late-arriving metadata
                   can't resize the bubble and jolt the scroll position. */
                height: 260,
                maxWidth: "100%",
                minWidth: 140,
                borderRadius: "8px",
                display: "block",
                backgroundColor: "#000",
              }}
            />
          ) : msg.messageType === "documentMessage" && msg.mediaUrl ? (
            <Box
              component="a"
              href={msg.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: iconColor,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Box sx={{ color: iconColor }}>{mediaConfig.icon}</Box>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.accent,
                }}
              >
                {msg.message || "Open document"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ color: iconColor }}>{mediaConfig.icon}</Box>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.secondary,
                }}
              >
                {mediaConfig.label}
              </Typography>
            </Box>
          )}
          {msg.message && msg.messageType !== "documentMessage" && (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.primary,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {msg.message}
            </Typography>
          )}
          {timestamp}
        </>
      ) : (
        <>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {msg.message}
          </Typography>
          {timestamp}
        </>
      )}
    </Box>
  );

  return bubbleNode;
};

const MessageGroupRow = ({
  group,
  audioCtx,
}: {
  group: MessageGroup;
  audioCtx?: WhatsappAudioPlaybackContext;
}) => {
  const theme = useTheme();
  const isAgent = group.senderRole === WhatsappSenderRole.Agent;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
      {group.messages.map((msg, idx) => {
        const isAudio = msg.messageType === "audioMessage";
        const isLastInGroup = idx === group.messages.length - 1;
        const position: MessageBubbleProps["position"] =
          group.messages.length === 1
            ? "only"
            : idx === 0
              ? "first"
              : isLastInGroup
                ? "last"
                : "middle";

        return (
          <Box
            key={`${msg.id}-${msg.time}`}
            sx={{
              display: "flex",
              flexDirection: isAgent ? "row-reverse" : "row",
              alignItems: "flex-end",
              gap: "6px",
              width: "100%",
            }}
          >
            <SenderAvatar name={msg.senderName} isAgent={isAgent} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isAgent ? "flex-end" : "flex-start",
                gap: "2px",
                flex: isAudio ? 1 : "0 1 auto",
                /* voice notes take the row minus the avatar column (32px + 6px gap) */
                maxWidth: isAudio
                  ? `min(calc(100% - 38px), ${BUBBLE_MAX_WIDTH})`
                  : `min(76%, ${BUBBLE_MAX_WIDTH})`,
                minWidth: 0,
              }}
            >
              {idx === 0 && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.semibold,
                    color: theme.nawy.colors.semantic.text.secondary,
                    px: "2px",
                  }}
                >
                  {msg.senderName}
                </Typography>
              )}
              <MessageBubble
                msg={msg}
                isAgent={isAgent}
                position={position}
                audioCtx={audioCtx}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Thread                                                                     */
/* -------------------------------------------------------------------------- */

interface WhatsappMessageThreadProps {
  messages: WhatsappMessage[];
  /** Show a "Load older messages" button — omit where a scroll container
      loads older pages on its own. */
  hasOlder?: boolean;
  isLoadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** Stretch the card to absorb its container's leftover height instead of
      hugging the messages. The container must be a flex column for this to
      take effect. */
  fillHeight?: boolean;
}

/**
 * The WhatsApp conversation thread exactly as the lead page activity drawer
 * renders it: day splitters, sender-grouped bubbles, media and voice notes.
 * Shared with the Chats & Calls panel so both surfaces stay identical.
 */
const WhatsappMessageThread = ({
  messages,
  hasOlder = false,
  isLoadingOlder = false,
  onLoadOlder,
  fillHeight = false,
}: WhatsappMessageThreadProps) => {
  const theme = useTheme();

  // Voice-note playback shared across all bubbles
  const audioCtx = useWhatsappAudioPlayback(messages);

  // Group messages by day for date dividers
  const sections = useMemo(() => {
    if (messages.length === 0) return [];
    const byDay: Array<{ dayIso: string; groups: MessageGroup[] }> = [];
    messages.forEach((msg) => {
      const last = byDay[byDay.length - 1];
      if (last && isSameDay(parseISO(last.dayIso), parseISO(msg.time))) {
        last.groups = groupMessages([
          ...last.groups.flatMap((g) => g.messages),
          msg,
        ]);
      } else {
        byDay.push({ dayIso: msg.time, groups: groupMessages([msg]) });
      }
    });
    return byDay;
  }, [messages]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.xs,
        px: theme.nawy.spacing.sm,
        py: theme.nawy.spacing.xs,
        borderRadius: CARD_RADIUS,
        backgroundColor: "#F7F5F4",
        // grow into the space, but never shrink below the messages
        ...(fillHeight && { flex: "1 0 auto" }),
      }}
    >
      {/* "Load older messages" button */}
      {onLoadOlder && hasOlder && messages.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: "4px" }}>
          <Button
            size="small"
            onClick={onLoadOlder}
            disabled={isLoadingOlder}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              color: theme.nawy.colors.semantic.text.accent,
              "&:hover": { background: "transparent", opacity: 0.8 },
            }}
          >
            {isLoadingOlder ? (
              <CircularProgress size={14} />
            ) : (
              "Load older messages"
            )}
          </Button>
        </Box>
      )}

      {/* Messages */}
      {messages.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.lg,
            textAlign: "center",
          }}
        >
          <MessageSquareOff
            size={28}
            color={theme.nawy.colors.semantic.text.tertiary}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            No messages yet
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          }}
        >
          {sections.map((section) => (
            <Box
              /* keyed by calendar date, not index — older pages prepend */
              key={`section-${section.dayIso.slice(0, 10)}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.xs,
              }}
            >
              <DaySplitter dayIso={section.dayIso} />
              {section.groups.map((group) => (
                <MessageGroupRow
                  key={`group-${group.messages[0]?.id}-${group.messages[0]?.time}`}
                  group={group}
                  audioCtx={audioCtx}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WhatsappMessageThread;
